import React, { useState, useEffect } from "react";
import logo from "../../assets/images/80eightLogo.png";
import { Input } from "../../components/ui/input-field";
import google from "../../assets/icons/google.svg";
import apple from "../../assets/icons/apple.svg";

import Toaster from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";

export default function Login() {
  const { toast } = useToast();
  const { signInUser, isAuthenticated } = useAuth();
  const { loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGoogleLogin = () => {
    loginWithRedirect({
      authorizationParams: {
        connection: "google-oauth2",
        prompt: "select_account",
        redirect_uri: `${window.location.origin}/login`,
      },
    });
  };

  const handleAppleLogin = () => {
    loginWithRedirect({
      authorizationParams: {
        connection: "apple",
        redirect_uri: `${window.location.origin}/login`,
      },
    });
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    if (error === "unauthorized" && errorDescription) {
      const description = decodeURIComponent(errorDescription).toLowerCase();
      if (
        description.includes("blocked") ||
        description.includes("user is blocked")
      ) {
        navigate("/account/blocked", { replace: true });
        return;
      }
    }
  }, [location.search, navigate]);

  // Redirect authenticated users (social auth callback handled in AuthContext)
  useEffect(() => {
    // Don't redirect if user is in 2FA flow
    const pendingId = localStorage.getItem("pendingId");
    if (isAuthenticated && !pendingId) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      try {
        const userData = await signInUser(email, password);

        // Check if 2FA is required
        if (userData?.requiresTwoFA) {
          toast({
            description: "Please verify 2FA code to complete login",
            variant: "success",
          });
          navigate("/login/2FA-auth");
          return;
        }

        toast({
          description: "Login successful.",
          variant: "success",
        });

        // Navigate based on user state
        if (!userData.emailVerified) {
          navigate("/onboarding/verify-email");
        } else if (!userData.profile_completed) {
          navigate("/complete-profile");
        } else {
          navigate("/");
        }
      } catch (error) {
        console.log(error);

        if (error.message === "USER_BLOCKED" || error.isBlocked) {
          navigate("/account/blocked");
          return;
        }

        toast({
          description: error.message || "Failed to log in",
          variant: "error",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen text-gray-900 transition-colors dark:text-gray-100">
      <div className="flex flex-col w-full h-screen bg-black">
        <div className="flex flex-col items-center justify-center h-full mx-auto">
          <form
            onSubmit={handleSubmit}
            className="bg-white border-[#F1EDF7] gap-5 lg:w-[477px] md:w-[477px] w-[363px] rounded-[14px] lg:p-12 md:p-12 p-4 flex flex-col justify-center items-center"
          >
            <img src={logo} alt="" className="w-[92px] h-[92px]" />
            <div>
              <h1 className="font-plusjakarta text-text-primary font-semibold text-[20px] leading-[24px] text-center">
                Log in to 80eight
              </h1>
              <p className="font-plusjakarta text-text-secondary p-2 text-[14px] text-center">
                Enter your details below to log in
              </p>
            </div>

            <div className="flex flex-col gap-5 w-full">
              <div className="flex flex-col gap-3">
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  state={errors.email ? "error" : "default"}
                  helperText={errors.email || ""}
                  className="!py-[27px] border-[1px] text-[16px] font-plusjakarta"
                />
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  state={errors.password ? "error" : "default"}
                  helperText={errors.password || ""}
                  className="!py-[27px] border-[1px] text-[16px] font-plusjakarta"
                />
              </div>

              <Link
                to="/forget-email"
                className="self-start font-semibold font-plusjakarta text-[14px] text-[#33125F] mb-2"
              >
                Forgot Password?
              </Link>

              <button
                type="submit"
                disabled={isLoading}
                className="font-plusjakarta px-6 py-3 mt-2 text-base font-semibold rounded-lg text-white bg-[#33125F] hover:bg-[#33115F] disabled:opacity-70 focus:outline-none flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Logging...
                  </>
                ) : (
                  "Log in"
                )}
              </button>

              <p className="self-start">
                <span className="font-plusjakarta text-[#423E51] text-sm">
                  Don't have an account?
                </span>
                <Link
                  className="font-semibold text-sm text-[#33125F] font-plusjakarta ml-3 cursor-pointer"
                  to="/signup"
                >
                  Create account
                </Link>
              </p>

              <div className="flex items-center w-full">
                <div className="flex-grow border-t border-black/10" />
                <span className="mx-4 text-[16px] text-[#6D6584]">OR</span>
                <div className="flex-grow border-t border-black/10" />
              </div>

              <button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full px-5 py-[13px] text-[#6D6584] flex items-center gap-2 rounded-lg border border-gray-300 bg-white text-sm font-plusjakarta font-semibold hover:bg-gray-50"
              >
                <img src={google} alt="Google" className="w-5 h-5" />
                Continue with Google
              </button>

              <button
                type="button"
                onClick={handleAppleLogin}
                className="w-full px-5 py-[13px] text-[#6D6584] flex items-center gap-2 rounded-lg border border-gray-300 bg-white text-sm font-plusjakarta font-semibold hover:bg-gray-50"
              >
                <img src={apple} alt="Apple" className="w-5 h-5" />
                Continue with Apple
              </button>

              {/* <button
                type="button"
                onClick={handlePasskeyLogin}
                className="w-full px-5 py-[13px] text-[#6D6584] flex items-center gap-2 rounded-lg border border-gray-300 bg-white text-sm font-plusjakarta font-semibold hover:bg-gray-50"
              >
                <img src={apple} alt="Apple" className="w-5 h-5" />
                Continue with Passkey
              </button> */}
            </div>
          </form>
        </div>
      </div>
      <Toaster />
    </div>
  );
}
